import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminService } from '../services/adminService';

const JIRA_BASE = 'https://remote-assistant.atlassian.net/browse';

const SCENARIO_OPTIONS = ['booking', 'info', 'cancel', 'modify', 'escalation', 'gateway'];

function formatDuration(seconds) {
  if (!seconds && seconds !== 0) return '--';
  const s = Math.round(Number(seconds));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m > 0) return `${m}m ${rem}s`;
  return `${rem}s`;
}

function formatMinutes(min) {
  if (!min && min !== 0) return '--';
  const m = Math.round(Number(min));
  if (m >= 60) return `${Math.floor(m / 60)}h ${m % 60}m`;
  return `${m}m`;
}

function ResultBadge({ result }) {
  const r = (result || '').toUpperCase();
  const isPassed = r === 'PASS' || r === 'PASSED';
  const isFailed = r === 'FAIL' || r === 'FAILED';
  const isSkipped = r === 'SKIP' || r === 'SKIPPED';
  const bg = isPassed ? '#d1fae5' : isFailed ? '#fee2e2' : isSkipped ? '#f3f4f6' : '#fef3c7';
  const color = isPassed ? '#065f46' : isFailed ? '#991b1b' : isSkipped ? '#374151' : '#92400e';
  return (
    <span className="inline-flex items-center px-2 py-0.5 text-xs font-inter font-medium"
      style={{ backgroundColor: bg, color, borderRadius: '12px', whiteSpace: 'nowrap' }}>
      {r || 'UNKNOWN'}
    </span>
  );
}

function StatusDot({ color }) {
  return (
    <span style={{
      display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
      backgroundColor: color, marginRight: 6, flexShrink: 0
    }} />
  );
}

export default function AIOpsConfig() {
  const { signOut } = useAuth();

  const [status, setStatus] = useState(null);
  const [triageResults, setTriageResults] = useState([]);
  const [investigations, setInvestigations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ phone: '', scenario: '', status: 'all', days: 7 });
  const [expandedTriage, setExpandedTriage] = useState(null);
  const [expandedInvestigation, setExpandedInvestigation] = useState(null);
  const [investigationLog, setInvestigationLog] = useState('');
  const [toggling, setToggling] = useState(false);
  const [billing, setBilling] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  const [debugLog, setDebugLog] = useState([]);
  const [selectedTickets, setSelectedTickets] = useState(new Set());
  const [closedTickets, setClosedTickets] = useState(new Set());
  const [closing, setClosing] = useState(false);

  const log = useCallback((msg) => {
    const ts = new Date().toLocaleTimeString();
    setDebugLog(prev => [...prev.slice(-200), `[${ts}] ${msg}`]);
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      log('Fetching status + billing...');
      const [s, b] = await Promise.all([
        adminService.getAIOpsStatus(),
        adminService.getAIOpsBilling(),
      ]);
      setStatus(s);
      setBilling(b);
      log(`Status loaded: paused=${s?.triage_paused}, investigator=${s?.investigator?.status}, stats=${JSON.stringify(s?.stats || {})}`);
    } catch (e) {
      log(`Status error: ${e.message}`);
    }
  }, [log]);

  const loadTriageResults = useCallback(async () => {
    try {
      const params = { days: filters.days };
      if (filters.phone) params.phone = filters.phone;
      if (filters.scenario) params.scenario = filters.scenario;
      log(`Fetching triage results (days=${params.days}, phone=${params.phone || 'all'}, scenario=${params.scenario || 'all'})...`);
      const data = await adminService.getTriageResults(params);
      const results = data.results || data || [];
      setTriageResults(results);
      log(`Loaded ${results.length} triage results`);
    } catch (e) {
      log(`Triage results error: ${e.message}`);
    }
  }, [filters, log]);

  const loadInvestigations = useCallback(async () => {
    try {
      log('Fetching investigations...');
      const data = await adminService.getInvestigations();
      const inv = data.investigations || data || [];
      setInvestigations(inv);
      log(`Loaded ${inv.length} investigations`);
    } catch (e) {
      log(`Investigations error: ${e.message}`);
    }
  }, [log]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([loadStatus(), loadTriageResults(), loadInvestigations()]);
    } catch (e) {
      setError(e.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [loadStatus, loadTriageResults, loadInvestigations]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    const interval = setInterval(loadStatus, 30000);
    return () => clearInterval(interval);
  }, [loadStatus]);

  useEffect(() => { loadTriageResults(); }, [loadTriageResults]);

  const handleToggleTriage = async () => {
    setToggling(true);
    try {
      const action = !status?.triage_paused ? 'pause' : 'resume';
      log(`Toggling triage: ${action}...`);
      if (!status?.triage_paused) {
        await adminService.pauseTriage();
      } else {
        await adminService.resumeTriage();
      }
      await loadStatus();
      log(`Triage ${action}d successfully`);
    } catch (e) {
      setError(e.message || 'Failed to toggle triage');
      log(`Toggle error: ${e.message}`);
    } finally {
      setToggling(false);
    }
  };

  const handleExpandInvestigation = async (ticket, attempt) => {
    const key = `${ticket}_${attempt}`;
    if (expandedInvestigation === key) {
      setExpandedInvestigation(null);
      setInvestigationLog('');
      return;
    }
    setExpandedInvestigation(key);
    setInvestigationLog('Loading...');
    log(`Loading investigation log: ${ticket} attempt ${attempt}`);
    try {
      const data = await adminService.getInvestigationLog(ticket, attempt);
      setInvestigationLog(data.log || data || 'No log available');
      log(`Loaded log for ${ticket} (${typeof data === 'string' ? data.length : JSON.stringify(data).length} chars)`);
    } catch (e) {
      setInvestigationLog(`Error loading log: ${e.message}`);
      log(`Log error: ${e.message}`);
    }
  };

  const toggleTicketSelection = (ticketKey) => {
    setSelectedTickets(prev => {
      const next = new Set(prev);
      if (next.has(ticketKey)) next.delete(ticketKey);
      else next.add(ticketKey);
      return next;
    });
  };

  const selectAllTickets = () => {
    const allTickets = filteredTriageResults.filter(r => r.jira_ticket).map(r => r.jira_ticket);
    if (selectedTickets.size === allTickets.length) {
      setSelectedTickets(new Set());
    } else {
      setSelectedTickets(new Set(allTickets));
    }
  };

  const handleCloseTickets = async (resolution) => {
    if (selectedTickets.size === 0) return;
    setClosing(true);
    const tickets = Array.from(selectedTickets);
    log(`Closing ${tickets.length} tickets as "${resolution}": ${tickets.join(', ')}`);
    try {
      const data = await adminService.closeTickets(tickets, resolution);
      log(`Close result: ${data.closed}/${data.total} closed`);
      if (data.results) {
        data.results.forEach(r => {
          if (r.status === 'error') log(`  ${r.ticket}: ERROR - ${r.detail}`);
          else log(`  ${r.ticket}: ${r.status}`);
        });
      }
      // Track successfully closed tickets so they're hidden from the list
      if (data.results) {
        const justClosed = data.results.filter(r => r.status === 'closed').map(r => r.ticket);
        setClosedTickets(prev => new Set([...prev, ...justClosed]));
      }
      setSelectedTickets(new Set());
    } catch (e) {
      setError(e.message || 'Failed to close tickets');
      log(`Close error: ${e.message}`);
    } finally {
      setClosing(false);
    }
  };

  const filteredTriageResults = triageResults.filter(r => {
    if (filters.status === 'passed' && !['PASS', 'passed'].includes(r.result)) return false;
    if (filters.status === 'failed' && !['FAIL', 'failed'].includes(r.result)) return false;
    if (r.jira_ticket && closedTickets.has(r.jira_ticket)) return false;
    return true;
  });

  const stats = status?.stats || {};
  const investigatorStatus = status?.investigator || {};
  const triageActive = !status?.triage_paused;

  const heartbeat = status?.investigator_heartbeat || {};
  let invStatusLabel = 'Stopped';
  let invStatusColor = '#979ba4';
  if (heartbeat.heartbeat_status === 'investigating' && heartbeat.ticket) {
    invStatusLabel = `Investigating ${heartbeat.ticket}`;
    invStatusColor = '#055efd';
  } else if ((investigatorStatus.runningCount || 0) > 0) {
    invStatusLabel = heartbeat.heartbeat_status === 'standby' ? 'Standby' : 'Running';
    invStatusColor = '#16a34a';
  } else if ((investigatorStatus.desiredCount || 0) > 0) {
    invStatusLabel = 'Starting...';
    invStatusColor = '#f59e0b';
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f9f9fb' }}>
      {/* Nav -- mobile responsive */}
      <nav className="bg-white shadow-sm" style={{ borderBottom: '1px solid #e5e7eb' }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between" style={{ minHeight: 56 }}>
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <img src="/assets/logo---dark-with-color.png" alt="Remote Assistant" className="h-7 sm:h-8 w-auto flex-shrink-0" />
              <Link to="/dashboard" className="text-xs sm:text-sm font-inter flex-shrink-0" style={{ color: '#055efd' }}>
                &larr; Back
              </Link>
              <h1 className="text-base sm:text-xl font-montserrat font-bold truncate" style={{ color: '#222324' }}>
                AI Operations
              </h1>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={loadData}
                className="font-inter text-xs sm:text-sm font-medium px-2 sm:px-3 py-1.5 rounded-lg transition-colors"
                style={{ color: '#055efd', border: '1px solid #055efd' }}
                onMouseEnter={(e) => { e.target.style.backgroundColor = '#055efd'; e.target.style.color = '#fff'; }}
                onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = '#055efd'; }}
              >
                Refresh
              </button>
              <button
                onClick={signOut}
                className="font-inter text-xs sm:text-sm font-medium px-2 sm:px-3 py-1.5 rounded-md transition-colors"
                style={{ color: '#979ba4' }}
                onMouseEnter={(e) => e.target.style.color = '#222324'}
                onMouseLeave={(e) => e.target.style.color = '#979ba4'}
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto py-4 sm:py-6 px-3 sm:px-6 lg:px-8">
        {/* Error banner */}
        {error && (
          <div className="mb-4 p-3 rounded-xl text-sm font-inter"
            style={{ backgroundColor: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>
            {error}
            <button onClick={() => setError('')} className="ml-2 font-medium underline">Dismiss</button>
          </div>
        )}

        {/* Loading state */}
        {loading && !status && (
          <div className="text-center py-12">
            <p className="text-sm font-inter" style={{ color: '#979ba4' }}>Loading AI Operations data...</p>
          </div>
        )}

        {/* Section 1: Controls & Status -- 2x2 grid on mobile, 4-col on desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
          {/* Triage Control */}
          <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm" style={{ border: '1px solid #e5e7eb' }}>
            <p className="text-xs sm:text-sm font-inter mb-2 sm:mb-3" style={{ color: '#979ba4' }}>Triage Control</p>
            <button
              onClick={handleToggleTriage}
              disabled={toggling || !status}
              className="w-full py-2 px-3 rounded-xl text-xs sm:text-sm font-inter font-semibold transition-colors"
              style={{
                backgroundColor: toggling ? '#e5e7eb' : triageActive ? '#d1fae5' : '#fef3c7',
                color: toggling ? '#979ba4' : triageActive ? '#065f46' : '#92400e',
                border: `1px solid ${toggling ? '#e5e7eb' : triageActive ? '#bbf7d0' : '#fde68a'}`,
                cursor: toggling ? 'wait' : 'pointer',
              }}
            >
              {toggling ? '...' : triageActive ? 'ACTIVE' : 'PAUSED'}
            </button>
            <p className="text-[10px] sm:text-xs font-inter mt-1.5 text-center" style={{ color: '#979ba4' }}>
              Click to {triageActive ? 'pause' : 'resume'}
            </p>
          </div>

          {/* Investigator Status */}
          <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm" style={{ border: '1px solid #e5e7eb' }}>
            <p className="text-xs sm:text-sm font-inter mb-2 sm:mb-3" style={{ color: '#979ba4' }}>Investigator</p>
            <div className="flex items-center mb-1">
              <StatusDot color={invStatusColor} />
              <span className="text-xs sm:text-sm font-inter font-semibold truncate" style={{ color: '#222324' }}>
                {invStatusLabel}
              </span>
            </div>
            {investigatorStatus.desiredCount !== undefined && (
              <p className="text-[10px] sm:text-xs font-inter" style={{ color: '#979ba4' }}>
                ECS: {investigatorStatus.runningCount || 0}/{investigatorStatus.desiredCount || 0} tasks
              </p>
            )}
            {heartbeat.pipeline_step && (
              <p className="text-[10px] sm:text-xs font-inter mt-0.5" style={{ color: '#979ba4' }}>
                Step: {heartbeat.pipeline_step}
              </p>
            )}
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm" style={{ border: '1px solid #e5e7eb' }}>
            <p className="text-xs sm:text-sm font-inter mb-2 sm:mb-3" style={{ color: '#979ba4' }}>Stats (30d)</p>
            <div className="space-y-1.5">
              {[
                ['Validated', stats.calls_validated],
                ['Detected', stats.issues_detected],
                ['Resolved', stats.issues_resolved],
                ['Avg resolve', stats.avg_resolution_min ? formatMinutes(stats.avg_resolution_min) : null],
              ].map(([label, val]) => (
                <div key={label} className="flex justify-between items-center">
                  <span className="text-[10px] sm:text-xs font-inter" style={{ color: '#979ba4' }}>{label}</span>
                  <span className="text-xs sm:text-sm font-inter font-semibold" style={{ color: '#222324' }}>
                    {val ?? '--'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Investigator Balance */}
          <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm" style={{ border: '1px solid #e5e7eb' }}>
            <p className="text-xs sm:text-sm font-inter mb-2 sm:mb-3" style={{ color: '#979ba4' }}>Inv. Cost</p>
            <p className="text-xl sm:text-3xl font-montserrat font-semibold" style={{ color: '#222324' }}>
              {billing?.total_spent !== undefined ? `$${Number(billing.total_spent).toFixed(2)}` : '--'}
            </p>
            <p className="text-[10px] sm:text-xs font-inter mt-1" style={{ color: '#979ba4' }}>
              Balance: {billing?.balance !== undefined ? `$${Number(billing.balance).toFixed(2)}` : '--'}
            </p>
          </div>
        </div>

        {/* Section 2: Recent Triage Results */}
        <div className="bg-white rounded-2xl shadow-sm mb-4 sm:mb-6" style={{ border: '1px solid #e5e7eb' }}>
          <div className="p-4 sm:p-6 pb-3 sm:pb-4" style={{ borderBottom: '1px solid #e5e7eb' }}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base sm:text-lg font-montserrat font-semibold" style={{ color: '#222324' }}>
                Recent Triage Results
              </h2>
              {selectedTickets.size > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-inter" style={{ color: '#979ba4' }}>
                    {selectedTickets.size} selected
                  </span>
                  <button onClick={() => handleCloseTickets("Won't Fix")} disabled={closing}
                    className="px-3 py-1.5 text-xs font-inter font-medium rounded-lg"
                    style={{ backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>
                    {closing ? '...' : "Won't Fix"}
                  </button>
                  <button onClick={() => handleCloseTickets("Invalid")} disabled={closing}
                    className="px-3 py-1.5 text-xs font-inter font-medium rounded-lg"
                    style={{ backgroundColor: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>
                    {closing ? '...' : "Invalid"}
                  </button>
                  <button onClick={() => handleCloseTickets("Done")} disabled={closing}
                    className="px-3 py-1.5 text-xs font-inter font-medium rounded-lg"
                    style={{ backgroundColor: '#d1fae5', color: '#065f46', border: '1px solid #bbf7d0' }}>
                    {closing ? '...' : "Done"}
                  </button>
                </div>
              )}
            </div>
            {/* Filter row -- wraps on mobile */}
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2 sm:gap-3">
              <input
                type="text"
                placeholder="Phone number"
                value={filters.phone}
                onChange={(e) => setFilters(f => ({ ...f, phone: e.target.value }))}
                className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-inter rounded-lg col-span-2 sm:col-span-1"
                style={{ border: '1px solid #e5e7eb', color: '#222324', width: '100%', maxWidth: 180 }}
              />
              <select
                value={filters.scenario}
                onChange={(e) => setFilters(f => ({ ...f, scenario: e.target.value }))}
                className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-inter rounded-lg"
                style={{ border: '1px solid #e5e7eb', color: '#222324' }}
              >
                <option value="">All scenarios</option>
                {SCENARIO_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select
                value={filters.status}
                onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
                className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-inter rounded-lg"
                style={{ border: '1px solid #e5e7eb', color: '#222324' }}
              >
                <option value="all">All results</option>
                <option value="passed">Passed</option>
                <option value="failed">Failed</option>
              </select>
              <select
                value={filters.days}
                onChange={(e) => setFilters(f => ({ ...f, days: Number(e.target.value) }))}
                className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-inter rounded-lg"
                style={{ border: '1px solid #e5e7eb', color: '#222324' }}
              >
                <option value={1}>Last 1 day</option>
                <option value={7}>Last 7 days</option>
                <option value={14}>Last 14 days</option>
                <option value={30}>Last 30 days</option>
              </select>
            </div>
          </div>

          {/* Mobile: card list. Desktop: table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full" style={{ minWidth: 700 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <th className="px-3 py-3 w-8">
                    <input type="checkbox" onChange={selectAllTickets}
                      checked={selectedTickets.size > 0 && selectedTickets.size === filteredTriageResults.filter(r => r.jira_ticket).length}
                      style={{ accentColor: '#055efd' }} />
                  </th>
                  {['Time', 'Phone', 'Scenario', 'Result', 'Failures', 'Jira', 'Session'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-inter font-medium"
                      style={{ color: '#979ba4' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredTriageResults.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-sm font-inter" style={{ color: '#979ba4' }}>
                      No triage results found
                    </td>
                  </tr>
                )}
                {filteredTriageResults.map((r, i) => {
                  const isExpanded = expandedTriage === r.session_id;
                  let checkpoints = [];
                  if (isExpanded && r.checkpoint_results) {
                    try {
                      checkpoints = typeof r.checkpoint_results === 'string'
                        ? JSON.parse(r.checkpoint_results) : r.checkpoint_results;
                      if (!Array.isArray(checkpoints)) checkpoints = Object.entries(checkpoints).map(([k, v]) => ({ name: k, ...v }));
                    } catch { checkpoints = []; }
                  }
                  return (
                    <React.Fragment key={r.session_id || i}>
                      <tr onClick={() => setExpandedTriage(isExpanded ? null : r.session_id)}
                        style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9f9fb'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                        <td className="px-3 py-3 w-8">
                          {r.jira_ticket && (
                            <input type="checkbox" checked={selectedTickets.has(r.jira_ticket)}
                              onChange={(e) => { e.stopPropagation(); toggleTicketSelection(r.jira_ticket); }}
                              onClick={(e) => e.stopPropagation()}
                              style={{ accentColor: '#055efd' }} />
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs font-inter" style={{ color: '#222324', whiteSpace: 'nowrap' }}>
                          {r.timestamp ? new Date(r.timestamp).toLocaleString() : '--'}
                        </td>
                        <td className="px-4 py-3 text-xs font-inter" style={{ color: '#222324', fontFamily: 'monospace' }}>
                          {r.phone || '--'}
                        </td>
                        <td className="px-4 py-3 text-xs font-inter" style={{ color: '#222324' }}>{r.scenario || '--'}</td>
                        <td className="px-4 py-3"><ResultBadge result={r.result} /></td>
                        <td className="px-4 py-3 text-xs font-inter" style={{ color: '#222324' }}>
                          {r.failure_count ?? r.failures ?? '--'}
                        </td>
                        <td className="px-4 py-3 text-xs font-inter">
                          {r.jira_ticket ? (
                            <a href={`${JIRA_BASE}/${r.jira_ticket}`} target="_blank" rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()} style={{ color: '#055efd' }}>{r.jira_ticket}</a>
                          ) : <span style={{ color: '#979ba4' }}>--</span>}
                        </td>
                        <td className="px-4 py-3 text-[10px] font-inter" style={{ color: '#979ba4', fontFamily: 'monospace' }}>
                          {r.session_id ? r.session_id.substring(0, 12) + '...' : '--'}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr><td colSpan={8} className="px-4 py-3" style={{ backgroundColor: '#f9f9fb' }}>
                          <CheckpointTable checkpoints={checkpoints} />
                        </td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="sm:hidden">
            {filteredTriageResults.length === 0 && (
              <p className="px-4 py-8 text-center text-sm font-inter" style={{ color: '#979ba4' }}>
                No triage results found
              </p>
            )}
            {filteredTriageResults.map((r, i) => {
              const isExpanded = expandedTriage === r.session_id;
              let checkpoints = [];
              if (isExpanded && r.checkpoint_results) {
                try {
                  checkpoints = typeof r.checkpoint_results === 'string'
                    ? JSON.parse(r.checkpoint_results) : r.checkpoint_results;
                  if (!Array.isArray(checkpoints)) checkpoints = Object.entries(checkpoints).map(([k, v]) => ({ name: k, ...v }));
                } catch { checkpoints = []; }
              }
              return (
                <div key={r.session_id || i} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <div className="px-4 py-3" onClick={() => setExpandedTriage(isExpanded ? null : r.session_id)}
                    style={{ cursor: 'pointer' }}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        {r.jira_ticket && (
                          <input type="checkbox" checked={selectedTickets.has(r.jira_ticket)}
                            onChange={(e) => { e.stopPropagation(); toggleTicketSelection(r.jira_ticket); }}
                            onClick={(e) => e.stopPropagation()}
                            style={{ accentColor: '#055efd' }} />
                        )}
                        <span className="text-xs font-inter" style={{ color: '#979ba4' }}>
                          {r.timestamp ? new Date(r.timestamp).toLocaleString() : '--'}
                        </span>
                      </div>
                      <ResultBadge result={r.result} />
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-inter font-medium" style={{ color: '#222324' }}>
                        {r.scenario || '--'}
                      </span>
                      <span className="text-[10px] font-inter" style={{ color: '#979ba4', fontFamily: 'monospace' }}>
                        {r.phone || ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      {r.jira_ticket && (
                        <a href={`${JIRA_BASE}/${r.jira_ticket}`} target="_blank" rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs font-inter" style={{ color: '#055efd' }}>{r.jira_ticket}</a>
                      )}
                      {(r.failure_count || r.failures) && (
                        <span className="text-[10px] font-inter" style={{ color: '#991b1b' }}>
                          {r.failure_count ?? r.failures} failures
                        </span>
                      )}
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="px-4 py-3" style={{ backgroundColor: '#f9f9fb' }}>
                      <CheckpointTable checkpoints={checkpoints} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Section 3: Investigation History */}
        <div className="bg-white rounded-2xl shadow-sm mb-4" style={{ border: '1px solid #e5e7eb' }}>
          <div className="p-4 sm:p-6 pb-3 sm:pb-4" style={{ borderBottom: '1px solid #e5e7eb' }}>
            <h2 className="text-base sm:text-lg font-montserrat font-semibold" style={{ color: '#222324' }}>
              Investigation History
            </h2>
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full" style={{ minWidth: 600 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  {['Ticket', 'Attempt', 'Duration', 'Tools', 'Result', 'Time'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-inter font-medium"
                      style={{ color: '#979ba4' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {investigations.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-sm font-inter" style={{ color: '#979ba4' }}>
                      No investigations found
                    </td>
                  </tr>
                )}
                {investigations.map((inv, i) => {
                  const key = `${inv.ticket_key}_${inv.attempt}`;
                  const isExpanded = expandedInvestigation === key;
                  return (
                    <React.Fragment key={key || i}>
                      <tr onClick={() => handleExpandInvestigation(inv.ticket_key, inv.attempt)}
                        style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9f9fb'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                        <td className="px-4 py-3 text-xs font-inter">
                          <a href={`${JIRA_BASE}/${inv.ticket_key}`} target="_blank" rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()} style={{ color: '#055efd' }}>{inv.ticket_key}</a>
                        </td>
                        <td className="px-4 py-3 text-xs font-inter" style={{ color: '#222324' }}>{inv.attempt ?? '--'}</td>
                        <td className="px-4 py-3 text-xs font-inter" style={{ color: '#222324' }}>
                          {inv.elapsed_seconds ? formatDuration(inv.elapsed_seconds) : '--'}
                        </td>
                        <td className="px-4 py-3 text-xs font-inter" style={{ color: '#222324' }}>
                          {inv.tool_calls_count ?? inv.tool_calls ?? '--'}
                        </td>
                        <td className="px-4 py-3 text-xs font-inter" style={{ color: '#222324', maxWidth: 200 }}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: 200 }}>
                            {inv.result || '--'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs font-inter" style={{ color: '#979ba4', whiteSpace: 'nowrap' }}>
                          {inv.timestamp ? new Date(inv.timestamp).toLocaleString() : '--'}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr><td colSpan={6} className="px-4 py-3" style={{ backgroundColor: '#f9f9fb' }}>
                          <p className="text-xs font-inter font-semibold mb-2" style={{ color: '#222324' }}>Session Log</p>
                          <pre className="text-[10px] sm:text-xs p-3 rounded-lg overflow-auto"
                            style={{ backgroundColor: '#1e1e1e', color: '#d4d4d4', fontFamily: 'monospace',
                              maxHeight: 400, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {investigationLog}
                          </pre>
                        </td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="sm:hidden">
            {investigations.length === 0 && (
              <p className="px-4 py-8 text-center text-sm font-inter" style={{ color: '#979ba4' }}>
                No investigations found
              </p>
            )}
            {investigations.map((inv, i) => {
              const key = `${inv.ticket_key}_${inv.attempt}`;
              const isExpanded = expandedInvestigation === key;
              return (
                <div key={key || i} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <div className="px-4 py-3" onClick={() => handleExpandInvestigation(inv.ticket_key, inv.attempt)}
                    style={{ cursor: 'pointer' }}>
                    <div className="flex items-center justify-between mb-1">
                      <a href={`${JIRA_BASE}/${inv.ticket_key}`} target="_blank" rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-sm font-inter font-medium" style={{ color: '#055efd' }}>{inv.ticket_key}</a>
                      <span className="text-[10px] font-inter" style={{ color: '#979ba4' }}>
                        Attempt {inv.attempt ?? '?'}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-[10px] font-inter" style={{ color: '#979ba4' }}>
                      <span>{inv.elapsed_seconds ? formatDuration(inv.elapsed_seconds) : '--'}</span>
                      <span>{inv.tool_calls_count ?? inv.tool_calls ?? '--'} tools</span>
                      <span>{inv.timestamp ? new Date(inv.timestamp).toLocaleDateString() : ''}</span>
                    </div>
                    {inv.result && (
                      <p className="text-[10px] font-inter mt-1 truncate" style={{ color: '#222324' }}>{inv.result}</p>
                    )}
                  </div>
                  {isExpanded && (
                    <div className="px-3 py-3" style={{ backgroundColor: '#f9f9fb' }}>
                      <pre className="text-[9px] p-2 rounded-lg overflow-auto"
                        style={{ backgroundColor: '#1e1e1e', color: '#d4d4d4', fontFamily: 'monospace',
                          maxHeight: 300, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {investigationLog}
                      </pre>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Debug Console -- following PlaygroundDiagnostics pattern */}
        <div className="mt-4 mb-8">
          <button onClick={() => setShowDebug(!showDebug)}
            className="font-inter" style={{ fontSize: 10, color: '#979ba4' }}
            onMouseEnter={(e) => e.target.style.color = '#222324'}
            onMouseLeave={(e) => e.target.style.color = '#979ba4'}>
            {showDebug ? 'Hide' : 'Show'} Debug Console
          </button>
          {showDebug && (
            <div className="mt-1 rounded-lg p-3 overflow-y-auto" style={{
              backgroundColor: '#111827', maxHeight: 256, fontFamily: 'monospace'
            }}>
              {debugLog.length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: 10 }}>No events logged yet.</p>
              ) : (
                debugLog.map((line, i) => (
                  <pre key={i} style={{ color: '#4ade80', fontSize: 10, whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0, lineHeight: 1.6 }}>{line}</pre>
                ))
              )}
              <div className="flex gap-2 mt-2 pt-2" style={{ borderTop: '1px solid #374151' }}>
                <button onClick={() => { navigator.clipboard.writeText(debugLog.join('\n')); }}
                  style={{ fontSize: 10, color: '#60a5fa' }}>Copy All</button>
                <button onClick={() => setDebugLog([])}
                  style={{ fontSize: 10, color: '#f87171' }}>Clear</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CheckpointTable({ checkpoints }) {
  if (!checkpoints || checkpoints.length === 0) {
    return <p className="text-xs font-inter" style={{ color: '#979ba4' }}>No checkpoint data available</p>;
  }
  return (
    <div>
      <p className="text-xs font-inter font-semibold mb-2" style={{ color: '#222324' }}>Checkpoint Details</p>
      <div className="space-y-1">
        {checkpoints.map((cp, j) => (
          <div key={j} className="flex items-start gap-2">
            <div className="flex-shrink-0 mt-0.5"><ResultBadge result={cp.result || cp.status} /></div>
            <div className="min-w-0">
              <span className="text-xs font-inter font-medium" style={{ color: '#222324' }}>
                {cp.name || cp.checkpoint || `Checkpoint ${j + 1}`}
              </span>
              {(cp.details || cp.message) && (
                <p className="text-[10px] font-inter" style={{ color: '#979ba4', wordBreak: 'break-word' }}>
                  {cp.details || cp.message}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
